/**
 * The requirements register — the obligations a contractor is actually held to.
 *
 * This surface already handled a FAILED read correctly: `classified`
 * short-circuits to a SectionError before the list renders. What it did not
 * handle was a read that SUCCEEDED and could not be understood.
 *
 *   const requirements = list?.requirements || [];
 *
 * `|| []` is not a shape guard. It catches `null` and `undefined` and nothing
 * else, so a `requirements` field arriving as an object or a string passed
 * straight through and threw a TypeError on the next `.map` or `.filter`,
 * during render, taking the tab down. Measured across the frontend, that exact
 * non-guard appears 91 times in 35 files; this module closes it for the
 * register and gives the register's derived logic — which had no test of any
 * kind — a home where it can be pinned.
 *
 * The read states come from the journey presenter. This module adds no
 * vocabulary of its own.
 */
import {
  REFUSED,
  UNREADABLE,
  READ,
  readCollection,
  NOT_ESTABLISHED_LABEL,
} from '../journey/journeyPresentation';

export { REFUSED, UNREADABLE, READ, NOT_ESTABLISHED_LABEL };

/**
 * Provenance the register treats as weak: the obligation is recorded, but
 * nothing in a document has been cited for it and no human has confirmed it.
 * An assumption a contractor is held to is worth counting separately from one
 * that carries evidence.
 */
export const WEAK_PROVENANCE = Object.freeze(['INFERRED', 'UNVERIFIED', 'OPERATOR_ENTERED']);

export const EVIDENCED = Object.freeze(['DOCUMENT_CITED', 'HUMAN_VERIFIED']);

export const STATUS_GROUPS = Object.freeze({
  all: null,
  open: [
    'IDENTIFIED',
    'PREPARING',
    'SUBMITTED',
    'UNDER_REVIEW',
    'RETURNED_FOR_REVISION',
    'RESUBMITTED',
    'REJECTED',
  ],
  in_review: ['SUBMITTED', 'UNDER_REVIEW', 'RESUBMITTED'],
  cleared: ['APPROVED', 'APPROVED_AS_NOTED', 'WAIVED'],
  inactive: ['SUPERSEDED', 'CANCELLED'],
});

/** A register row is readable when it is an object carrying an id. */
export const requirementRowIsReadable = (row) =>
  Boolean(row) && typeof row === 'object' && !Array.isArray(row) && Boolean(row.id);

/**
 * Read the register envelope.
 *
 * The payload is `{requirements: [...]}`, so BOTH the envelope and the list
 * inside it can be the wrong shape, independently. An envelope that is not an
 * object is unreadable; an envelope whose `requirements` is not a list is
 * equally unreadable, and neither is an empty register.
 */
export const readRegister = (payload, { requestFailed = false } = {}) => {
  if (requestFailed) {
    return {
      state: REFUSED,
      items: [],
      unreadableRows: 0,
      reason: 'The register did not load, so which obligations exist is unknown.',
    };
  }
  if (!payload || typeof payload !== 'object' || Array.isArray(payload)) {
    return {
      state: UNREADABLE,
      items: [],
      unreadableRows: 0,
      reason: 'The register response was not an object. MALFORMED IS NOT EMPTY.',
    };
  }
  const read = readCollection(payload.requirements, {
    rowIsReadable: requirementRowIsReadable,
  });
  if (read.state === UNREADABLE) {
    return {
      state: UNREADABLE,
      items: [],
      unreadableRows: 0,
      reason: 'The register did not contain a list of requirements. MALFORMED IS NOT EMPTY.',
    };
  }
  return { state: READ, items: read.items, unreadableRows: read.unreadableRows, reason: null };
};

const matchesProvenance = (row, provenance) => {
  if (provenance === 'evidenced') return EVIDENCED.includes(row.verificationStatus);
  if (provenance === 'assumption') return WEAK_PROVENANCE.includes(row.verificationStatus);
  if (provenance === 'conflict') return row.verificationStatus === 'CONFLICTING';
  return true;
};

/**
 * Apply the register's filters. Pure, and total: it is only ever handed the
 * readable rows, so it cannot be the thing that throws.
 */
export const filterRequirements = (
  items,
  { statusGroup = 'all', typeFilter = 'all', hardGatesOnly = false, provenanceFilter = 'all', search = '' } = {},
) => {
  if (!Array.isArray(items)) return [];
  const statuses = STATUS_GROUPS[statusGroup];
  const needle = String(search || '').toLowerCase();
  return items.filter((row) => {
    if (statuses && !statuses.includes(row.status)) return false;
    if (typeFilter !== 'all' && row.requirementType !== typeFilter) return false;
    if (hardGatesOnly && !row.isHardGate) return false;
    if (!matchesProvenance(row, provenanceFilter)) return false;
    if (needle && !`${row.title ?? ''} ${row.requirementType ?? ''}`.toLowerCase().includes(needle)) {
      return false;
    }
    return true;
  });
};

/** The type dropdown's options, from the rows that could actually be read. */
export const requirementTypes = (read) => {
  if (!read || read.state !== READ) return [];
  const set = new Set();
  for (const row of read.items) {
    if (typeof row.requirementType === 'string' && row.requirementType) set.add(row.requirementType);
  }
  return [...set].sort();
};

/**
 * How many obligations rest on an assumption.
 *
 * NOT ESTABLISHED unless the register was actually read. "0 assumptions" is a
 * reassuring thing to tell a contractor, and it must not be said on the basis
 * of a payload nobody could parse.
 */
export const assumptionCount = (read) => {
  if (!read || read.state !== READ) return { established: false, value: null };
  return {
    established: true,
    value: read.items.filter((row) => WEAK_PROVENANCE.includes(row.verificationStatus)).length,
  };
};

export const countLabel = (fact) =>
  fact && fact.established ? String(fact.value) : NOT_ESTABLISHED_LABEL;

/**
 * What the list area should say. `rows` is the filtered result, so an empty
 * register and a filter that matched nothing stay distinguishable — they were
 * already distinct here, and they remain so.
 */
export const registerView = (read, rows) => {
  if (!read || read.state === REFUSED) {
    return {
      kind: 'REFUSED',
      heading: 'The requirements register could not be read',
      body: read?.reason || 'The register did not load, so which obligations exist is unknown.',
    };
  }
  if (read.state === UNREADABLE) {
    return { kind: 'UNREADABLE', heading: 'The requirements register could not be read', body: read.reason };
  }
  if (read.items.length === 0) {
    return {
      kind: 'EMPTY',
      heading: 'No requirements recorded for this project yet',
      body:
        'Requirements are ingested from contract documents (or entered by an operator) through the register API. Once recorded, every obligation appears here with its evidence.',
    };
  }
  if (!rows || rows.length === 0) {
    return {
      kind: 'NO_MATCH',
      heading: 'Nothing matches these filters',
      body: 'Clear a filter to see the rest of the register.',
    };
  }
  return { kind: 'POPULATED', heading: null, body: null };
};
